import { useEffect, useMemo, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate, useParams } from "react-router-dom";
import { deleteLead, editLead, getLead, getLeadServices, getLocations, updateLead, updateLeadServices } from "../services/api";
import { track } from "../services/analytics";
import { hoursSinceCreated } from "../utils/leadUtils";
import { appointmentHelp } from "../utils/helpContent";
import AboutLead from "./AboutLead";
import AppointmentForm from "./AppointmentForm";
import ContactedModal from "./ContactedModal";
import HelpTooltip from "./HelpTooltip";
import ConversationCard from "./ConversationCard";
import LeadAppointmentList from "./LeadAppointmentList";
import LeadHeroSection from "./LeadHeroSection";
import LeadNavigationButtons from "./LeadNavigationButtons";
import LeadNotes from "./LeadNotes";
import NoteCreate from "./NoteCreate";
import LeadStatusFlow from "./LeadStatusFlow";
import SidecartSection, {
  SectionAction,
  ShowAllButton,
} from "./SidecartSection";
import LossReasonModal from "./LossReasonModal";
import NoteDetail from "./NoteDetail";
import ObjectHistory from "./ObjectHistory";
import ReasonLine from "./ReasonLine";

// Header icons for the collapsible sections below.
const SECTION_ICON = {
  calendar:
    "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  note: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
  clock: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
};

const LeadDetailSidecart = ({
  authToken,
  lead: propLead,
  onLeadUpdate,
  setTitle,
  onClose = null,
  onMessagesRead,
}) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id: urlId } = useParams();
  const navigate = useNavigate();

  // State for inline editing
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [locations, setLocations] = useState([]);

  // State for note viewing
  const [selectedNoteId, setSelectedNoteId] = useState(null);

  // State for services
  const [leadServices, setLeadServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // State for loss reason modal
  const [isLossReasonModalOpen, setIsLossReasonModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null);
  // State for contacted modal
  const [isContactedModalOpen, setIsContactedModalOpen] = useState(false);

  // State for appointments
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentRefreshTrigger, setAppointmentRefreshTrigger] = useState(0);
  const [appointmentsExpanded, setAppointmentsExpanded] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // State for section counts. Seeded from the lead detail payload, then kept
  // live by the visible child components as the user adds notes/messages/etc.
  const [appointmentCount, setAppointmentCount] = useState(0);
  const [noteCount, setNoteCount] = useState(0);

  // Bumped after a send from the Conversation card so the messaging thread refetches.
  const [messageRefreshTrigger, setMessageRefreshTrigger] = useState(0);

  // State for status-triggered appointment scheduling
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // State for note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  // Remounts LeadNotes so it refetches after a note is saved.
  const [noteRefreshKey, setNoteRefreshKey] = useState(0);

  const effectiveId = propLead?.id || urlId;

  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(() => {
    return localStorage.getItem("hasInteractedWithFollowUpDate") === "true";
  });
  const [followUpDismissed, setFollowUpDismissed] = useState(() => {
    return localStorage.getItem("followUpFeatureDismissed") === "true";
  });

  const fetchLead = async () => {
    // For sidecart, always fetch fresh lead data to ensure we have latest estimated_value
    try {
      const [leadData, servicesData] = await Promise.all([
        getLead(authToken, propLead.id),
        getLeadServices(authToken, propLead.id),
      ]);
      setLead(leadData);
      setLeadServices(servicesData || []);
      setError(null);
      return leadData;
    } catch (err) {
      console.error("Error fetching lead for sidecart:", err);
      // Fallback to propLead if fetch fails
      setLead(propLead);
      setLeadServices([]);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLead();
  }, [authToken, effectiveId, propLead?.id]);

  // Fire lead_viewed once per lead — fetchLead also runs after every edit
  const trackedLeadId = useRef(null);
  useEffect(() => {
    if (!lead?.id || trackedLeadId.current === lead.id) return;
    trackedLeadId.current = lead.id;
    track("lead_viewed", {
      lead_id: lead.id,
      source: lead.source,
      type: lead.type,
      category: lead.lead_category,
      ai_priority: lead.ai_score,
      was_unread: !lead.is_read_by_user,
      surface: "sidecart",
    });
  }, [lead]);

  // Mark as interacted when the user opens the dropdown
  useEffect(() => {
    if (isOpen && !hasInteracted) {
      setHasInteracted(true);
      localStorage.setItem("hasInteractedWithFollowUpDate", "true");
    }
  }, [isOpen, hasInteracted]);

  const handleServicesChange = async (updatedServices) => {
    setServicesLoading(true);
    try {
      await updateLeadServices(authToken, effectiveId, updatedServices);
      setLeadServices(updatedServices);
      // Fetch updated estimated_value only
      const updatedLead = await getLead(authToken, effectiveId);
      setLead((prev) => ({
        ...prev,
        estimated_value: updatedLead.estimated_value,
      }));
      // Don't trigger full list refresh for service changes in sidecart
    } catch (error) {
      console.error("Error updating services:", error);
      setError("Failed to update services. Please try again.");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleNewServiceAdded = async (updatedServices, newService) => {
    setServicesLoading(true);
    try {
      await updateLeadServices(authToken, effectiveId, updatedServices);
      setLeadServices(updatedServices);
      // Just update the estimated_value without full lead refresh
      setLead((prev) => ({
        ...prev,
        estimated_value:
          prev.estimated_value + parseFloat(newService.price || 0),
      }));
    } catch (error) {
      console.error("Error updating services:", error);
      setError("Failed to update services. Please try again.");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleDismissFollowUp = () => {
    setFollowUpDismissed(true);
    localStorage.setItem("followUpFeatureDismissed", "true");
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const fetchedLocations = await getLocations(authToken);
        setLocations(fetchedLocations);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setError("Failed to fetch locations. You can still edit other fields.");
      }
    };
    fetchLocations();
  }, [authToken]);

  const currentLead = lead || propLead;

  // Seed section counts from the detail payload. These used to come from three
  // hidden copies of LeadMessaging/LeadNotes/LeadAppointmentList, which double-
  // fetched everything and — because LeadMessaging marks messages read on mount
  // — silently cleared unread state on leads the user never opened messaging on.
  useEffect(() => {
    if (lead?.note_count !== undefined) setNoteCount(lead.note_count);
    if (lead?.appointment_count !== undefined) setAppointmentCount(lead.appointment_count);
  }, [lead?.note_count, lead?.appointment_count]);

  const nameOrPhone = useMemo(() => {
    const fullName = `${currentLead?.first_name || ""} ${
      currentLead?.last_name || ""
    }`.trim();

    if (fullName) {
      return fullName;
    }

    return currentLead?.phone || "";
  }, [currentLead]);

  // A staff text to a waiting lead also moves status new -> contacted on the
  // server; mirror it so the pill clears without a refetch. Only new leads
  // need a callback, so that move clears it too.
  const handleStaffTextSent = () => {
    if (currentLead?.waiting_reply) {
      const status = currentLead.status === "new" ? "contacted" : currentLead.status;
      setLead((prev) => ({ ...prev, waiting_reply: false, status, needs_callback: false }));
      if (onLeadUpdate) onLeadUpdate(currentLead.id, { status, waiting_reply: false });
    }
  };

  const handleActRowSent = () => {
    handleStaffTextSent();
    setMessageRefreshTrigger((prev) => prev + 1);
  };

  const handleAddNote = () => {
    setShowNoteForm(true);
  };

  const handleNoteCreated = () => {
    setShowNoteForm(false);
    setNotesExpanded(true);
    setNoteRefreshKey((prev) => prev + 1);
  };

  const trackStatusChange = (fromStatus, toStatus) => {
    track("lead_status_changed", {
      lead_id: currentLead.id,
      from_status: fromStatus,
      to_status: toStatus,
      hours_since_created: hoursSinceCreated(currentLead),
    });
    if (toStatus === "converted") {
      track("lead_converted", {
        lead_id: currentLead.id,
        days_to_convert: hoursSinceCreated(currentLead) / 24,
      });
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!currentLead?.id) {
      setError("Cannot update status: No lead ID available.");
      return;
    }

    // Check if the new status requires a loss reason
    if (newStatus === "lost") {
      setPendingStatus(newStatus);
      setIsLossReasonModalOpen(true);
      return;
    }

    // Check if the new status is "contacted" and show modal
    if (newStatus === "contacted") {
      setIsContactedModalOpen(true);
      return;
    }

    // For other statuses, update directly
    try {
      await updateLead(authToken, currentLead.id, { status: newStatus });

      trackStatusChange(currentLead.status, newStatus);

      // Update the lead status locally instead of refetching all data.
      // Only new leads need a callback, so a status change clears it.
      setLead((prevLead) => ({
        ...prevLead,
        status: newStatus,
        needs_callback: false,
      }));

      // Don't trigger external refresh in sidecart mode to prevent modal dismissal
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update lead status. Please try again.");
    }
  };

  const handleCategoryChange = async (newCategory) => {
    if (!currentLead?.id) return;

    if (newCategory === "not_applicable") {
      setPendingCategory(newCategory);
      setIsLossReasonModalOpen(true);
      return;
    }

    try {
      await updateLead(authToken, currentLead.id, { lead_category: newCategory });
      setLead((prevLead) => ({ ...prevLead, lead_category: newCategory }));
    } catch (err) {
      console.error("Error updating lead category:", err);
      setError("Failed to update lead category. Please try again.");
    }
  };

  // Staff correction of the AI's next action. Stored in next_action, which wins over
  // ai_next_action — re-analysis keeps updating the AI's own field without clobbering this.
  const handleNextActionChange = async (newAction) => {
    if (!currentLead?.id) return;
    const value = newAction || null;
    try {
      await updateLead(authToken, currentLead.id, { next_action: value });
      setLead((prevLead) => ({
        ...prevLead,
        next_action: value,
        effective_next_action: value || prevLead.ai_next_action,
      }));
    } catch (err) {
      console.error("Error updating next action:", err);
      setError("Could not save the next action. Please try again.");
    }
  };

  const handleLossReasonSave = async (status, category, lossReason, lossReasonNotes) => {
    try {
      const updateData = { loss_reason: lossReason, loss_reason_notes: lossReasonNotes };
      if (category === "not_applicable") {
        updateData.lead_category = "not_applicable";
        updateData.status = "lost";
      } else {
        updateData.status = status;
      }
      await updateLead(authToken, currentLead.id, updateData);
      trackStatusChange(currentLead.status, updateData.status);
      track("lead_lost", {
        lead_id: currentLead.id,
        loss_reason: lossReason,
      });
      await fetchLead();
      if (onLeadUpdate) {
        onLeadUpdate(currentLead.id);
      }
    } catch (err) {
      console.error("Error saving loss reason:", err);
      setError("Failed to update lead. Please try again.");
      throw err;
    } finally {
      setPendingStatus(null);
      setPendingCategory(null);
    }
  };

  const handleLossReasonModalClose = () => {
    setIsLossReasonModalOpen(false);
    setPendingStatus(null);
    setPendingCategory(null);
  };

  const handleContactedModalClose = () => {
    setIsContactedModalOpen(false);
  };

  const handleContactedModalSave = async (status) => {
    try {
      await updateLead(authToken, currentLead.id, {
        status: status,
      });
      trackStatusChange(currentLead.status, status);
      await fetchLead();
      if (onLeadUpdate) {
        onLeadUpdate(currentLead.id);
      }
      setIsContactedModalOpen(false);
    } catch (error) {
      console.error("Failed to update lead status:", error);
      setError("Failed to update lead status. Please try again.");
    }
  };

  // Appointment handlers
  const handleAppointmentCreated = async () => {
    setAppointmentRefreshTrigger((prev) => prev + 1);
    setShowAppointmentForm(false);

    // Refresh lead data to get updated status from backend
    await fetchLead();
    if (onLeadUpdate) {
      onLeadUpdate(currentLead.id);
    }
  };

  const handleScheduleModalAppointmentCreated = async () => {
    setAppointmentRefreshTrigger((prev) => prev + 1);
    setShowScheduleModal(false);

    // Refresh lead data to get updated status from backend
    await fetchLead();
    if (onLeadUpdate) {
      onLeadUpdate(currentLead.id);
    }
  };

  const handleScheduleAppointment = () => {
    setShowScheduleModal(true);
  };

  const handleCreateNote = () => {
    setShowNoteForm(true);
  };

  const handleDeleteLead = async () => {
    if (!currentLead?.id) {
      setError("Cannot delete lead: No lead ID available.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this lead?")) {
      try {
        await deleteLead(authToken, currentLead.id);
        navigate("/leads");
      } catch (error) {
        console.error("Error deleting lead:", error);
        setError("Failed to delete lead. Please try again.");
      }
    }
  };

  const startEditing = (field, initialValue) => {
    setEditingField(field);
    setEditValue(initialValue);
  };

  const handleEditChange = (value) => {
    setEditValue(value);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };

  const saveEdit = async (field, valueOverride = null) => {
    try {
      const valueToUse = valueOverride !== null ? valueOverride : editValue;
      let updateData = {};
      if (field === "phone") {
        updateData = { phone: valueToUse };
      } else if (field === "email") {
        updateData = { email: valueToUse };
      } else if (field === "location") {
        // The serializer exposes `location` read-only; `location_id` is the
        // writable field, so a `location` key here is silently dropped.
        updateData = {
          location_id: valueToUse === "" ? null : parseInt(valueToUse, 10),
        };
      } else if (field === "first_name") {
        updateData = { first_name: valueToUse };
      } else if (field === "last_name") {
        updateData = { last_name: valueToUse };
      } else if (field === "name") {
        // Split the full name into first and last name
        const nameParts = valueToUse.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        updateData = {
          first_name: firstName,
          last_name: lastName,
        };
      } else if (field === "source") {
        updateData = { source: valueToUse };
      } else if (field === "insurance_provider") {
        updateData = { insurance_provider: valueToUse };
      } else if (field === "follow_up_date") {
        updateData = {
          follow_up_date:
            valueToUse && valueToUse instanceof Date
              ? valueToUse.toISOString().split("T")[0]
              : null,
        };
      }

      await editLead(authToken, currentLead.id, updateData);
      const updatedLead = await fetchLead();
      if (onLeadUpdate) {
        onLeadUpdate(currentLead.id, updatedLead);
      }
      setEditingField(null);
      setEditValue("");
    } catch (error) {
      console.error(`Error updating lead ${field}:`, error);
      setError(`Failed to update ${field}. Please try again.`);
    }
  };

  // Handle note selection
  const handleNoteSelect = (noteId) => {
    setSelectedNoteId(noteId);
  };

  // Handle closing the note view
  const handleCloseNote = () => {
    setSelectedNoteId(null);
  };

  // Handle note update (refresh notes list)
  const handleNoteUpdated = () => {
    fetchLead();
  };

  // Handle note deletion (close note view and refresh)
  const handleNoteDeleted = () => {
    setSelectedNoteId(null);
    fetchLead();
  };

  // Dynamically update the side cart title
  // useEffect(() => {
  //   if (setTitle) {
  //     if (selectedNoteId) {
  //       setTitle(<span>Note Details</span>);
  //     } else if (currentLead) {
  //       setTitle(
  //         <div className="flex items-center px-10 space-x-2 whitespace-nowrap">
  //           <span>{formatLeadTypeToIcon(currentLead.type)}</span>
  //           <span>{nameOrPhone}</span>
  //           <span> ({currentLead.request_count || 0})</span>
  //         </div>
  //       );
  //     } else {
  //       setTitle(<span>Lead Details</span>);
  //     }
  //   }
  // }, [selectedNoteId, setTitle, currentLead]);

  if (!currentLead)
    return <div className="text-center py-8">No lead found.</div>;

  // The header and the details are two parts of one hero, each drawn once.
  const heroProps = {
    currentLead,
    isSideCart: true,
    nameOrPhone,
    editingField,
    editValue,
    locations,
    leadServices,
    followUpDismissed,
    hasInteracted,
    isOpen,
    setIsOpen,
    startEditing,
    handleEditChange,
    cancelEditing,
    saveEdit,
    handleDismissFollowUp,
    currentCategory: currentLead.lead_category || "prospect",
    onCategoryChange: handleCategoryChange,
  };

  return (
    <div className="space-y-0">
      {selectedNoteId ? (
        <div className="absolute inset-0 bg-background z-20 overflow-y-auto">
          <NoteDetail
            authToken={authToken}
            leadId={effectiveId}
            noteId={selectedNoteId}
            onNoteUpdated={handleNoteUpdated}
            onNoteDeleted={handleNoteDeleted}
            onClose={handleCloseNote}
          />
        </div>
      ) : (
        <div className="relative">
          <LeadNavigationButtons
            currentLead={currentLead}
            isSideCart={true}
            onDeleteLead={handleDeleteLead}
          />

          <div className="">
            <div id="details" className="relative">
              {/* The header, the reason line and the status stepper stay
                  fixed. Everything else scrolls under them. */}
              <div data-testid="sidecart-sticky" className="sticky top-0 z-[150] bg-background pt-4 pb-3">
               {/* One card, three rows split by hairlines: identity, the
                   reason band, the stepper. No gap inside it. */}
               <div
                 data-testid="sidecart-header-card"
                 className="rounded-md border border-border bg-white relative overflow-visible"
               >
                <div className="flex items-start gap-2 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <LeadHeroSection {...heroProps} part="header" />
                  </div>
                  {onClose && (
                    <button
                      onClick={onClose}
                      className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-lg bg-primary/80 hover:bg-primary text-white transition-all"
                      aria-label="Close"
                    >
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>

                <ReasonLine
                  lead={currentLead}
                  onLeadUpdate={(updated) => {
                    setLead((prev) => ({ ...prev, ...updated }));
                    if (onLeadUpdate) onLeadUpdate(updated.id, { waiting_reply: false });
                  }}
                  embedded
                />

                <div className="border-t border-border px-4 py-3">
                  <LeadStatusFlow
                    size="large"
                    currentStatus={currentLead.status}
                    currentCategory={currentLead.lead_category || "prospect"}
                    onStatusChange={handleStatusChange}
                    onCategoryChange={handleCategoryChange}
                    onScheduleAppointment={handleScheduleAppointment}
                  />
                </div>
               </div>
              </div>

              <div className="space-y-3">
                {/* Who the patient is first, then what the AI read. */}
                <div className="space-y-3">
                  <LeadHeroSection {...heroProps} part="details" />
                  <AboutLead lead={currentLead} />
                </div>

                <ConversationCard
                  authToken={authToken}
                  lead={currentLead}
                  refreshTrigger={messageRefreshTrigger}
                  onSent={handleActRowSent}
                  onBook={handleScheduleAppointment}
                  onAddNote={handleAddNote}
                  onMessagesRead={onMessagesRead}
                  onNextActionChange={handleNextActionChange}
                />

                {/* Appointments Section */}
                <SidecartSection
                  id="appointments"
                  title="Appointments"
                  icon={SECTION_ICON.calendar}
                  count={appointmentCount}
                  expanded={appointmentsExpanded}
                  onToggle={() => setAppointmentsExpanded(!appointmentsExpanded)}
                  action={
                    <>
                      <HelpTooltip
                        title={appointmentHelp.appointmentsOverview.title}
                        content={appointmentHelp.appointmentsOverview.content}
                        size="lg"
                        position="top-right"
                      />
                      <SectionAction
                        open={showAppointmentForm}
                        label="New appointment"
                        title={
                          showAppointmentForm
                            ? "Cancel appointment"
                            : "New appointment"
                        }
                        onClick={() => {
                          if (!appointmentsExpanded) {
                            setAppointmentsExpanded(true);
                          }
                          setShowAppointmentForm(!showAppointmentForm);
                        }}
                      />
                    </>
                  }
                  preview={
                    appointmentCount > 0 ? (
                      <>
                        <div className="p-4 pb-2">
                          <LeadAppointmentList
                            authToken={authToken}
                            leadId={currentLead.id}
                            refreshTrigger={appointmentRefreshTrigger}
                            compact={true}
                            onCountChange={setAppointmentCount}
                            showFirstOnly={true}
                          />
                        </div>
                        {appointmentCount > 1 && (
                          <ShowAllButton
                            count={appointmentCount}
                            label="appointments"
                            onClick={() => setAppointmentsExpanded(true)}
                          />
                        )}
                      </>
                    ) : null
                  }
                >
                  {showAppointmentForm && (
                    <div className="mb-4">
                      <AppointmentForm
                        authToken={authToken}
                        leadId={currentLead.id}
                        onAppointmentCreated={handleAppointmentCreated}
                        onClose={() => setShowAppointmentForm(false)}
                        compact={true}
                      />
                    </div>
                  )}
                  <LeadAppointmentList
                    authToken={authToken}
                    leadId={currentLead.id}
                    refreshTrigger={appointmentRefreshTrigger}
                    compact={true}
                    onCountChange={setAppointmentCount}
                    showFirstOnly={false}
                  />
                </SidecartSection>

                {/* Lead Notes Section */}
                <SidecartSection
                  id="notes"
                  title="Notes"
                  icon={SECTION_ICON.note}
                  count={noteCount}
                  expanded={notesExpanded}
                  onToggle={() => setNotesExpanded(!notesExpanded)}
                  action={
                    <SectionAction
                      open={false}
                      label="New note"
                      title="New note"
                      onClick={handleCreateNote}
                    />
                  }
                  preview={
                    noteCount > 0 ? (
                      <>
                        <div className="p-4 pb-2">
                          <LeadNotes
                            key={noteRefreshKey}
                            authToken={authToken}
                            id={currentLead.id}
                            onNoteSelect={setSelectedNoteId}
                            onExpandRequest={() => setNotesExpanded(true)}
                            showFirstOnly={true}
                            onCountChange={setNoteCount}
                          />
                        </div>
                        {noteCount > 1 && (
                          <ShowAllButton
                            count={noteCount}
                            label="notes"
                            onClick={() => setNotesExpanded(true)}
                          />
                        )}
                      </>
                    ) : null
                  }
                >
                  <LeadNotes
                    key={noteRefreshKey}
                    authToken={authToken}
                    id={currentLead.id}
                    onNoteSelect={setSelectedNoteId}
                    onExpandRequest={() => setNotesExpanded(true)}
                    onCountChange={setNoteCount}
                  />
                </SidecartSection>

                {/* History Section */}
                <SidecartSection
                  id="history"
                  title="History"
                  icon={SECTION_ICON.clock}
                  expanded={historyExpanded}
                  onToggle={() => setHistoryExpanded(!historyExpanded)}
                >
                  <ObjectHistory
                    authToken={authToken}
                    objectType="lead"
                    objectId={currentLead.id}
                    compact={true}
                  />
                </SidecartSection>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loss Reason Modal */}
      <LossReasonModal
        isOpen={isLossReasonModalOpen}
        onClose={handleLossReasonModalClose}
        onSave={handleLossReasonSave}
        status={pendingStatus}
        category={pendingCategory}
        initialReason={currentLead?.loss_reason || ""}
        initialNotes={currentLead?.loss_reason_notes || ""}
      />

      {/* Contacted Modal */}
      <ContactedModal
        isOpen={isContactedModalOpen}
        onClose={handleContactedModalClose}
        onSave={handleContactedModalSave}
        authToken={authToken}
        leadId={currentLead?.id}
        currentFollowUpDate={currentLead?.follow_up_date}
        onLeadUpdate={(updateData) => {
          setLead((prevLead) => ({
            ...prevLead,
            ...updateData,
          }));
        }}
      />

      {/* Schedule Appointment Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-teal-800">
                    Schedule Appointment
                  </h3>
                </div>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full p-1 transition duration-300"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Schedule an appointment for this lead. Once created, the lead
                status will be updated to "Scheduled".
              </p>

              <AppointmentForm
                authToken={authToken}
                leadId={currentLead.id}
                onAppointmentCreated={handleScheduleModalAppointmentCreated}
                onClose={() => setShowScheduleModal(false)}
                compact={false}
              />
            </div>
          </div>
        </div>
      )}
      {/* Add Note Modal */}
      {showNoteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-teal-800">
                    Add Note
                  </h3>
                </div>
                <button
                  onClick={() => setShowNoteForm(false)}
                  className="text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full p-1 transition duration-300"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <NoteCreate
                authToken={authToken}
                contentType="leads"
                patientId={currentLead.id}
                onNoteCreated={handleNoteCreated}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetailSidecart;